import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsAuth } from "../../redux/slices/auth";
import axios from "../../axios";

import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import SimpleMDE from "react-simplemde-editor";

import "easymde/dist/easymde.min.css";
import styles from "./AddPost.module.scss";

export const AddPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAuth = useSelector(selectIsAuth);
  const [isLoading, setIsLoading] = useState(false);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [showVideoLinkInput, setShowVideoLinkInput] = useState(false);
  const [videoLink, setVideoLink] = useState("");
  const [additionalMedia, setAdditionalMedia] = useState([]);

  const videoFileRef = useRef(null);
  const inputFileRef = useRef(null);
  const isEditing = Boolean(id);

  const handleChangeFile = async (event) => {
    try {
      const formData = new FormData();
      const file = event.target.files[0];
      formData.append("image", file);
      const { data } = await axios.post("/upload", formData);
      setImageUrl(data.url);
    } catch (err) {
      console.warn(err);
      alert("Помилка під час завантаження файлу");
    }
  };

  const handleVideoChange = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 100 * 1024 * 1024) {
        alert("Розмір файлу перевищує 100 МБ.");
        return;
      }
      const formData = new FormData();
      formData.append("video", file);
      const { data } = await axios.post("/upload-video", formData);
      setVideoUrl(data.url);
    } catch (err) {
      if (err.response?.data?.error) alert(err.response.data.error);
      else alert("Помилка під час завантаження відео");
    }
  };

  const convertToEmbedUrl = (url) => {
    // Попробуем обработать как YouTube
    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch && youtubeMatch[1]) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Попробуем обработать как Vimeo
    const vimeoRegex = /vimeo\.com\/(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch && vimeoMatch[1]) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    // Для других сервисов возвращаем оригинальную ссылку
    return url;
  };

  // Обработчик для добавления видео по ссылке
  const handleAddVideoLink = () => {
    try {
      const url = new URL(videoLink);
      const embedUrl = convertToEmbedUrl(url.href);
      setVideoUrl(embedUrl);
      setShowVideoLinkInput(false);
      setVideoLink("");
      if (videoFileRef.current) videoFileRef.current.value = "";
    } catch (e) {
      alert("Будь ласка, введіть коректне URL відео");
    }
  };

  // Обработчик загрузки дополнительных медиа
  const handleAdditionalMedia = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + additionalMedia.length > 16) {
      alert("Можна завантажити до 16 файлів");
      return;
    }
    try {
      const newUrls = await Promise.all(
        files.map(async (file) => {
          const formData = new FormData();
          formData.append("image", file);
          const { data } = await axios.post("/upload", formData);
          return data.url;
        })
      );
      setAdditionalMedia([...additionalMedia, ...newUrls]);
    } catch (err) {
      console.error("Помилка завантаження:", err.response?.data || err.message);
      alert("Помилка завантаження файлів");
    }
  };

  const onClickRemoveImage = () => {
    setImageUrl("");
  };

  const onChange = useCallback((value) => {
    setText(value);
  }, []);

  const onSubmit = async () => {
    const errors = [];
    if (!title.trim()) errors.push("Не ввели заголовок статті");
    if (!text.trim()) errors.push("Не ввели текст статті");

    if (errors.length > 0) {
      alert(`Помилка під час створення статті:\n${errors.join("\n")}`);
      return;
    }
    try {
      setIsLoading(true);
      const fields = {
        title,
        imageUrl,
        videoUrl: videoUrl.startsWith("process.env.REACT_APP_API_URL")
          ? videoUrl.replace("process.env.REACT_APP_API_URL", "")
          : videoUrl,
        tags: tags.split(","),
        text,
        additionalMedia: additionalMedia.map((url) =>
          url.startsWith("process.env.REACT_APP_API_URL")
            ? url.replace("process.env.REACT_APP_API_URL", "")
            : url
        ),
      };

      const { data } = isEditing
        ? await axios.patch(`/posts/${id}`, fields)
        : await axios.post("/posts", fields);

      const _id = isEditing ? id : data._id;

      navigate(`/posts/${_id}`);
    } catch (err) {
      console.warn(err);
      const errorMessage =
        err.response?.data?.message || "Помилка під час створення статті";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      axios
        .get(`/posts/${id}`)
        .then(({ data }) => {
          setTitle(data.title);
          setText(data.text);
          setTags(data.tags.join(","));
          setImageUrl(data.imageUrl);
          setVideoUrl(data.videoUrl || "");
          setAdditionalMedia(data.additionalMedia || []);
        })
        .catch((err) => {
          console.warn(err);
          alert("Помилка при отриманні статті");
        });
    }
  }, [id]);

  const options = useMemo(
    () => ({
      spellChecker: false,
      maxHeight: "400px",
      autofocus: true,
      placeholder: "Введіть текст...",
      status: false,
      autosave: {
        enabled: true,
        delay: 1000,
      },
    }),
    []
  );

  if (!window.localStorage.getItem("token") && !isAuth) {
    return <Navigate to="/" />;
  }

  return (
    <Paper style={{ padding: 30 }}>
      <Button
        onClick={() => inputFileRef.current.click()}
        variant="outlined"
        size="large"
      >
        Завантажити головну картинку
      </Button>
      <input
        ref={inputFileRef}
        type="file"
        onChange={handleChangeFile}
        accept="image/*"
        hidden
      />
      {imageUrl && (
        <>
          <Button
            variant="contained"
            color="error"
            onClick={onClickRemoveImage}
            style={{ marginLeft: "20px" }}
          >
            Видалити
          </Button>
          <img className={styles.image} src={imageUrl} alt="Uploaded" />
        </>
      )}

      {/* Блок работы с видео */}
      <div style={{ marginTop: 20 }}>
        <Button
          onClick={() => videoFileRef.current.click()}
          variant="outlined"
          size="large"
          style={{ paddingRight: "52px" }}
        >
          Завантажити головне відео
        </Button>

        <input
          ref={videoFileRef}
          type="file"
          onChange={handleVideoChange}
          accept="video/*"
          hidden
        />

        <Button
          onClick={() => setShowVideoLinkInput(true)}
          variant="outlined"
          size="large"
          // style={{ marginLeft: "10px" }}
        >
          Заголовне відео за посиланням
        </Button>

        {showVideoLinkInput && (
          <div style={{ marginTop: 10 }}>
            <TextField
              value={videoLink}
              onChange={(e) => setVideoLink(e.target.value)}
              placeholder="Введіть посилання на відео (YouTube, Vimeo та ін.)"
              fullWidth
              variant="outlined"
            />
            <Button
              onClick={handleAddVideoLink}
              variant="contained"
              color="primary"
              style={{ marginTop: 10, marginRight: 10 }}
            >
              Додати
            </Button>
            <Button
              onClick={() => setShowVideoLinkInput(false)}
              variant="outlined"
              style={{ marginTop: 10 }}
            >
              Скасування
            </Button>
          </div>
        )}

        {videoUrl && (
          <>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setVideoUrl("");
                setVideoLink("");
                setShowVideoLinkInput(false);
              }}
              style={{ marginLeft: "20px" }}
            >
              Видалити відео
            </Button>
            {videoUrl.match(/\.(mp4|webm|ogg)$/i) ? (
              <video
                controls
                src={videoUrl}
                style={{ maxWidth: "100%", marginTop: "10px" }}
              />
            ) : (
              <iframe
                src={videoUrl}
                title="Video player"
                style={{
                  width: "100%",
                  height: "400px",
                  marginTop: 10,
                  border: "none",
                  borderRadius: "8px",
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </>
        )}
      </div>

      <Button
        variant="outlined"
        onClick={() => document.getElementById("additional-media").click()}
        style={{ margin: "20px 0" }}
      >
        Додати відео чи зображення (до 16)
      </Button>
      <input
        id="additional-media"
        type="file"
        multiple
        onChange={handleAdditionalMedia}
        hidden
        accept="image/*, video/*"
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        {additionalMedia.map((url, index) => {
          const isVideo = url.match(/\.(mp4|webm|ogg|mov|avi)$/i);
          return (
            <div key={index} style={{ width: "23%", position: "relative" }}>
              {isVideo ? (
                <video
                  controls
                  src={url}
                  style={{ width: "100%", height: 120, objectFit: "cover" }}
                />
              ) : (
                <img
                  src={url}
                  alt="Превью"
                  style={{ width: "100%", height: 120, objectFit: "cover" }}
                />
              )}
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() =>
                  setAdditionalMedia(
                    additionalMedia.filter((_, i) => i !== index)
                  )
                }
                style={{ position: "absolute", top: 5, right: 5 }}
              >
                ×
              </Button>
            </div>
          );
        })}
      </div>

      <br />
      <br />
      <TextField
        classes={{ root: styles.title }}
        variant="standard"
        placeholder="Заголовок статті..."
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
      />
      <TextField
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        classes={{ root: styles.tags }}
        variant="standard"
        placeholder="Теги (через кому, без пробілів)"
        fullWidth
      />
      <SimpleMDE
        className={styles.editor}
        value={text}
        onChange={onChange}
        options={options}
      />
      <div className={styles.buttons}>
        <Button
          onClick={onSubmit}
          size="large"
          variant="contained"
          disabled={isLoading} // Блокировка при загрузке
        >
          {isEditing ? "Зберегти" : "Опублікувати"}
        </Button>
        <a href="/">
          <Button size="large">Скасування</Button>
        </a>
      </div>
    </Paper>
  );
};
